import React from 'react';
import { VStack, HStack, Text, Badge, Button, Link, Icon } from '@chakra-ui/react';
import { FileText, Tag, Calendar, ExternalLink, Edit } from 'lucide-react';
import Drawer, { DrawerHeader, DrawerTitle, DrawerBody, DrawerFooter, DrawerCloseTrigger } from '@/components/ui/Drawer';
import DetailsSection from '@/components/ui/DetailsSection';
import LoadingSkeleton from '@/components/LoadingSkeleton';
import withHashDrawer from '@/components/ui/withHashDrawer';
import { BaseDetailsModalProps } from '@/types/DetailsModalTypes';
import { useGetConditionResourceLookupQuery } from '../api';
import { getStatusBadgeProps } from '@/utils/lookupStatus';

interface ConditionResourceDetailsModalProps extends BaseDetailsModalProps {
  // No additional props needed for ConditionResourceDetailsModal
}

const ConditionResourceDetailsModal: React.FC<ConditionResourceDetailsModalProps> = ({
  isOpen,
  onClose,
  entityId,
  navigateToModal,
}) => {
  // Fetch individual entity if entityId is provided (hash modal mode)
  const { data: selectedResource, isLoading } = useGetConditionResourceLookupQuery(entityId!, {
    skip: !entityId,
  });
  const renderContent = () => {
    if (isLoading) {
      return <LoadingSkeleton />;
    }

    if (!selectedResource) {
      return <Text>Resource not found</Text>;
    }

    return (
      <VStack gap={6} align="stretch">
        {/* Basic Information */}
        <DetailsSection
          title="Basic Information"
          icon={FileText}
          items={[
            {
              label: "ID",
              value: selectedResource.id
            },
            {
              label: "Title",
              value: selectedResource.title
            },
            {
              label: "Description",
              value: selectedResource.description,
              stacked: true
            },
            {
              label: "Type",
              value: selectedResource.type,
              render: () => (
                <Badge 
                  colorPalette="blue" 
                  size="sm"
                  variant="subtle"
                >
                  {selectedResource.type}
                </Badge>
              )
            },
            {
              label: "Resource Type",
              value: selectedResource.resource_type,
              render: () => (
                <Badge 
                  colorPalette="green" 
                  size="sm"
                  variant="subtle"
                >
                  {selectedResource.resource_type}
                </Badge>
              )
            },
            {
              label: "Status",
              value: selectedResource.status,
              render: () => {
                const { colorPalette, label } = getStatusBadgeProps(selectedResource.status);
                return (
                  <Badge colorPalette={colorPalette} size="sm" variant="subtle">
                    {label}
                  </Badge>
                );
              }
            },
            {
              label: "Validation Status",
              value: selectedResource.is_valid !== null ? (selectedResource.is_valid ? 'valid' : 'invalid') : 'unknown',
              render: () => (
                <Badge 
                  colorPalette={
                    selectedResource.is_valid === true ? 'green' : 
                    selectedResource.is_valid === false ? 'red' : 'gray'
                  }
                  size="sm"
                  variant="subtle"
                >
                  {selectedResource.is_valid === true ? 'Valid' : 
                   selectedResource.is_valid === false ? 'Invalid' : 'Unknown'}
                </Badge>
              )
            },
            {
              label: "URL",
              value: selectedResource.url,
              render: () => (
                <VStack align="end" gap={1}>
                  <Link
                    href={selectedResource.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    color="blue.600"
                    _dark={{ color: "blue.400" }}
                    fontSize="sm"
                    wordBreak="break-all"
                    textAlign="right"
                  >
                    {selectedResource.url}
                  </Link>
                </VStack>
              )
            }
          ]}
        />

        {/* Related Information */}
        <DetailsSection
          title="Related Information"
          icon={Tag}
          items={[
            {
              label: "Condition ID",
              value: selectedResource.condition_lookup_id,
              render: () => (
                <Link
                  href={`/conditions/#/${selectedResource.condition_lookup_id}/view`}
                  color="brand.500"
                  fontSize="sm"
                  fontWeight="medium"
                >
                  #{selectedResource.condition_lookup_id}
                </Link>
              )
            },
            {
              label: "Condition",
              value: selectedResource.condition_lookup?.name
            }
          ]}
        />

        {/* Timestamps */}
        <DetailsSection
          title="Timestamps"
          icon={Calendar}
          items={[
            {
              label: "Created",
              value: selectedResource.created_at,
              render: () => (
                <Text fontSize="sm">
                  {selectedResource.created_at ? 
                    new Date(selectedResource.created_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    }) : 'N/A'
                  }
                </Text>
              )
            },
            {
              label: "Updated",
              value: selectedResource.updated_at,
              render: () => (
                <Text fontSize="sm">
                  {selectedResource.updated_at ? 
                    new Date(selectedResource.updated_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    }) : 'N/A'
                  }
                </Text>
              )
            }
          ]}
        />
      </VStack>
    );
  };

  return (
    <Drawer isOpen={isOpen} onClose={onClose} size="lg">
      <DrawerHeader>
        <DrawerTitle color="brand.700" _dark={{ color: "brand.300" }}>
          Resource Details {selectedResource ? `- ${selectedResource.title}` : ''}
        </DrawerTitle>
        <DrawerCloseTrigger />
      </DrawerHeader>

      <DrawerBody>
        {renderContent()}
      </DrawerBody>

      <DrawerFooter>
        <HStack justify="space-between" w="full">
          <HStack>
            {entityId && navigateToModal && (
              <Button 
                variant="outline" 
                onClick={() => navigateToModal(entityId, 'edit')}
              >
                <Icon as={Edit} boxSize={4} mr={2} />
                Edit
              </Button>
            )}
            {selectedResource?.url && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => window.open(selectedResource.url, '_blank')}
              >
                <ExternalLink size={16} style={{ marginRight: 8 }} />
                Open Resource
              </Button>
            )}
          </HStack>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </HStack>
      </DrawerFooter>
    </Drawer>
  );
};

// Export wrapped version for hash modal (details modal: view action)
export default withHashDrawer(ConditionResourceDetailsModal, {
  action: 'view' // Default view action
}); 
